import type { RangeMatrix } from "~/models/range-matrix";
import { ceil } from "~/tools/ceil";
import {
	type Interval,
	addMilliseconds,
	differenceInMilliseconds,
	getTime,
	isAfter,
	subMilliseconds,
} from "~/tools/date-time";
import type { Time } from "~/tools/date-time/time";
import { first } from "~/tools/first";
import { identity } from "~/tools/identity";
import { last } from "~/tools/last";
import { max } from "~/tools/max";
import { min } from "~/tools/min";
import { isNonEmptyArray } from "~/tools/non-empty-array";
import { prop } from "~/tools/prop";
import { sortBy } from "~/tools/sort-by";
import { unique } from "~/tools/unique";

import type { TimeRange } from "../../app/state/view/timeline/TimeRange";

export interface ChartDataPoint {
	time: Time;
	values: Array<null | number>;
}

/**
 * TimeSeries do not map directly to chart data points for the following reasons:
 *  - Rechart needs null values in order to make on hover and on brush possible on empty areas. The time-
 *    series may contain gaps that must therefore be filled with null values.
 *  - The time range may be bigger or smaller than the time series. Again we need to generate null values
 *    for the reason stated above.
 */
export function createChartDataPoints(
	timeRange: TimeRange,
	timeSeries: ReadonlyArray<RangeMatrix>,
): Array<ChartDataPoint> {
	const allUniqTimestamps = mergeTimestamps(timeSeries.flatMap(prop("values")).map(prop(0)));

	if (!isNonEmptyArray(allUniqTimestamps)) {
		// in this case some type of no-data error will be given
		return [];
	}

	const step = findMinimalStep(allUniqTimestamps) ?? 0;

	// this happens when there is only a single point.
	// this causes a minor bug, see STAC-18650
	// but generating null points here causes another issue inside rechart :(
	if (step <= 0) {
		return [];
	}

	const timeRangeStart = timeRange.start();
	const timeRangeEnd = timeRange.endDate();

	const timeSeriesStart = first(allUniqTimestamps);
	const timeSeriesEnd = last(allUniqTimestamps);

	const startDelta = differenceInMilliseconds(timeSeriesStart, timeRangeStart);

	const idealTimestamps = generateTimestamps(
		{
			// start at a time aligned to the time series, before or at the time range
			start: subMilliseconds(timeSeriesStart, ceil(startDelta / step, 0) * step),
			end: max(timeSeriesEnd, timeRangeEnd),
		},
		step,
	);

	const timeMaps = timeSeries.map(({ values }) => new Map(values));

	return mergeTimestamps([...allUniqTimestamps, ...idealTimestamps]).map((time) => ({
		time,
		values: timeMaps.map((values) => values.get(time) ?? null),
	}));
}

function findMinimalStep(timestamps: ReadonlyArray<Time>) {
	const steps = timestamps
		.map((timestamp, index) => differenceInMilliseconds(timestamp, timestamps[index - 1]))
		// drop the first one since it has no step
		.slice(1);

	return min(steps);
}

function generateTimestamps({ start, end }: Interval, step: number): Array<number> {
	if (isAfter(start, end)) {
		return [];
	}

	const duration = differenceInMilliseconds(end, start);

	const count = ceil(duration / step, 0);
	// protect against infinite loop that can crash the browser & too large loop that can slow the browser down
	if (count > 5000) {
		return [];
	}

	const timestamps: Array<number> = [];

	for (let timestamp = start; !isAfter(timestamp, end); timestamp = addMilliseconds(timestamp, step)) {
		timestamps.push(getTime(timestamp));
	}

	return timestamps;
}

function mergeTimestamps(timestamps: ReadonlyArray<Time>): Array<number> {
	return sortBy(unique(timestamps.map((timestamp) => getTime(timestamp))), identity);
}
